import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, Switch, Table, message, Spin, Upload, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getEndpoint } from '../utils/endpoints';

const { Option } = Select;

interface Glossary {
  description: string;
  direction: string;
  glossary_code: string;
  glossary_name: string;
  domain: string;
  reversible: boolean;
}

interface GlossaryRow {
  source: string;
  target: string;
  priority: number;
  category: string;
  confirmed: boolean;
  donotuse: boolean;
}

const GlossaryManagement: React.FC = () => {
  const [glossaries, setGlossaries] = useState<Glossary[]>([]);
  const [selectedGlossary, setSelectedGlossary] = useState<string | null>(null);
  const [selectedGlossaryDescription, setSelectedGlossaryDescription] = useState<string | null>(null);  
  const [filteredGlossaryData, setFilteredGlossaryData] = useState<GlossaryRow[]>([]);
  const [glossaryData, setGlossaryData] = useState<{ glossary: GlossaryRow[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddingGlossary, setIsAddingGlossary] = useState(false);
  const [editingRow, setEditingRow] = useState<GlossaryRow | null>(null);
  const [isRowModalVisible, setIsRowModalVisible] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [form] = Form.useForm();
  const [rowForm] = Form.useForm();

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      fetchGlossaries();
    }
  };

  useEffect(() => {
    fetchGlossaries();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!glossaryData?.glossary) {
      setFilteredGlossaryData([]);
      return;
    }

    const normalizedSource = sourceFilter.trim().toLowerCase();
    const normalizedTarget = targetFilter.trim().toLowerCase();

    const nextData = glossaryData.glossary.filter((row) => {
      const sourceValue = row.source ? row.source.toLowerCase() : '';
      const targetValue = row.target ? row.target.toLowerCase() : '';
      const matchesSource = !normalizedSource || sourceValue.includes(normalizedSource);
      const matchesTarget = !normalizedTarget || targetValue.includes(normalizedTarget);
      return matchesSource && matchesTarget;
    });

    setFilteredGlossaryData(nextData);
  }, [glossaryData, sourceFilter, targetFilter]);

  const fetchGlossaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${getEndpoint('GLOSS')}/getGlossaries/`);
      setGlossaries(response.data);
    } catch (error) {
      console.error('Error fetching glossaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedGlossary) {
      message.error('Please select a glossary to download.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${getEndpoint('GLOSS')}/downloadGlossary/`,
        {
          params: { glossary_name: selectedGlossary },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedGlossary}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading glossary:', error);
      message.error('Failed to download glossary.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file }: { file: File }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('glossary_name', selectedGlossary || '');
    setLoading(true);
    try {
      await axios.post(`${getEndpoint('GLOSS')}/uploadGlossary/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success('Glossary uploaded successfully!');
      fetchGlossaries();
    } catch (error) {
      console.error('Error uploading glossary:', error);
      message.error('Failed to upload glossary.');
    } finally {
      setLoading(false);
    }
  };
  const handleGlossarySelect = async (glossaryName: string) => {
    setLoading(true);
    try {
      setSelectedGlossary(glossaryName);
      const response = await axios.post(`${getEndpoint('GLOSS')}/getGlossary/`, {
        glossary_name: glossaryName,
      });
      setGlossaryData(response.data);
      setFilteredGlossaryData(response.data.glossary);
      setSourceFilter('');
      setTargetFilter('');
      const selectedGlossaryDescription = glossaries.find(glossary => glossary.glossary_name === glossaryName)?.description;
      setSelectedGlossaryDescription(selectedGlossaryDescription || '');
      setGlossaries((prev) => [...prev]);
    } catch (error) {
      console.error('Error fetching glossary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditGlossary = async (values: Glossary) => {
    setLoading(true);
    try {
      await axios.post(`${getEndpoint('GLOSS')}/addGlossary/`, values);
      message.success(isAddingGlossary ? 'Glossary added successfully!' : 'Glossary updated successfully!');
      fetchGlossaries();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving glossary:', error);
      message.error('Failed to save glossary.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGlossary = async () => {
    if (!selectedGlossary) return;
    setLoading(true);
    try {
      await axios.post(`${getEndpoint('GLOSS')}/deleteGlossary/`, {
        glossary_name: selectedGlossary,
      });
      message.success('Glossary deleted successfully!');
      setSelectedGlossary(null);
      setGlossaryData(null);
      setFilteredGlossaryData([]);
      setSourceFilter('');
      setTargetFilter('');
      fetchGlossaries();
    } catch (error) {
      console.error('Error deleting glossary:', error);
      message.error('Failed to delete glossary.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditRow = async (values: GlossaryRow) => {
    if (!selectedGlossary) return;
    setLoading(true);
    try {
      const endpoint = editingRow
        ? `${getEndpoint('GLOSS')}/editGlossaryRow/`
        : `${getEndpoint('GLOSS')}/addGlossaryRow/`;
      const payload = editingRow
        ? {
          glossary_name: selectedGlossary,
          previous_row: editingRow,
          row: values,
        }
        : {
          glossary_name: selectedGlossary,
          row: values,
        };
      await axios.post(endpoint, payload);
      message.success(editingRow ? 'Row updated successfully!' : 'Row added successfully!');
      handleGlossarySelect(selectedGlossary);
      setIsRowModalVisible(false);
    } catch (error) {
      console.error('Error saving row:', error);
      message.error('Failed to save row.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = async (row: GlossaryRow) => {
    if (!selectedGlossary) return;
    setLoading(true);
    try {
      await axios.post(`${getEndpoint('GLOSS')}/deleteGlossaryRow/`, {
        glossary_name: selectedGlossary,
        row,
      });
      message.success('Row deleted successfully!');
      handleGlossarySelect(selectedGlossary);
    } catch (error) {
      console.error('Error deleting row:', error);
      message.error('Failed to delete row.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (value: string) => value || 'generic',
    },    
    {
      title: 'Forbidden',
      dataIndex: 'donotuse',
      key: 'donotuse',
      render: (value: boolean) => (value ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GlossaryRow) => (
        <>
            <Button
            onClick={() => {
              setEditingRow(record);
              rowForm.setFieldsValue(record);
              setIsRowModalVisible(true);
            }}
            style={{ marginRight: '10px' }}
            >
            Edit
            </Button>
            <Button onClick={() => handleDeleteRow(record)} danger>
            Delete
            </Button>
        </>
      ),
    },
  ];

  return (
    <div>
    <Spin spinning={loading}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <Select
            showSearch
            placeholder="Select a glossary"
            style={{ width: 300 }}
            value={selectedGlossary}
            onChange={handleGlossarySelect}
            filterOption={(input, option) =>
              typeof option?.children === 'string' &&
              (option.children as string).toLowerCase().includes(input.toLowerCase())
            }
          >
            {glossaries.map((glossary) => (
              <Option key={glossary.glossary_name} value={glossary.glossary_name}>
                {glossary.glossary_name}
              </Option>
            ))}
          </Select>
          <Button
            onClick={() => {
              setIsAddingGlossary(true);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Glossary
          </Button>
          {selectedGlossary && (
            <>
              <Button onClick={handleDeleteGlossary} danger>
                Delete Glossary
              </Button>
              <Button onClick={handleDownload} icon={<DownloadOutlined />}>
                Download
              </Button>
              <Upload beforeUpload={(file) => handleUpload({ file })} showUploadList={false}>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </>
          )}
        </div>
        {selectedGlossary && (
            <>
            <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa' }}>
            <Typography.Text strong>Description: </Typography.Text>
            <Typography.Text>{selectedGlossaryDescription}</Typography.Text>
            <br />
            <Typography.Text strong>Domain: </Typography.Text>
            <Typography.Text>{glossaries.find(glossary => glossary.glossary_name === selectedGlossary)?.domain}</Typography.Text>
            <br />
            <Typography.Text strong>Direction: </Typography.Text>
            <Typography.Text>{glossaries.find(glossary => glossary.glossary_name === selectedGlossary)?.direction}</Typography.Text>
            <br />
            <Typography.Text strong>Bidirectional: </Typography.Text>
            <Typography.Text>{glossaries.find(glossary => glossary.glossary_name === selectedGlossary)?.reversible ? 'Yes' : 'No'}</Typography.Text>
            </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', width: '800px' }}>
            <Input.Search
              placeholder="Search by source text"
              allowClear
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{ width: 300 }}
            />
            <Input.Search
              placeholder="Search by target text"
              allowClear
              value={targetFilter}
              onChange={(e) => setTargetFilter(e.target.value)}
              style={{ width: 300 }}
            />

            <Button
              onClick={() => {
                setEditingRow(null);
                rowForm.resetFields();
                setIsRowModalVisible(true);
              }}
            >
              Add Row
            </Button>
          </div>
          </>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={filteredGlossaryData}
        rowKey={(record) =>
          `${record.source}-${record.target}-${record.priority}-${record.category || 'generic'}-${
            record.donotuse ? 'restricted' : 'allowed'
          }`
        }
      />
    </Spin>
  
    <Modal
      title={isAddingGlossary ? 'Add Glossary' : 'Edit Glossary'}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      onOk={() => form.submit()}
    >
      <Form form={form} onFinish={handleAddOrEditGlossary} layout="vertical">
        <Form.Item label="Glossary Name" name="glossary_name" rules={[{ required: true }]}>
          <Input disabled={!isAddingGlossary} />
        </Form.Item>
        <Form.Item label="Description" name="description" rules={[{ required: true }]}>
          <Input.TextArea />
        </Form.Item>
        <Form.Item label="Direction" name="direction" rules={[{ required: true }]}>
          <Select>
            <Option value="it-en">it-en</Option>
            <Option value="en-it">en-it</Option>
            <Option value="en-de">en-de</Option>
            <Option value="de-en">de-en</Option>
            <Option value="fr-it">fr-it</Option>
            <Option value="it-fr">it-fr</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Domain" name="domain" rules={[{ required: false }]}>
          <Select>
            <Option value="generic" selected>generic</Option>
            <Option value="patent">patent</Option>
            <Option value="legal">legal</Option>
            <Option value="travel">travel</Option>
            <Option value="technical">technical</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Bidirectional" name="reversible" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  
    <Modal
      title={editingRow ? 'Edit Row' : 'Add Row'}
      open={isRowModalVisible}
      onCancel={() => setIsRowModalVisible(false)}
      onOk={() => rowForm.submit()}
    >
      <Form form={rowForm} onFinish={handleAddOrEditRow} layout="vertical">
        <Form.Item label="Source" name="source" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Target" name="target" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Priority" name="priority" rules={[{ required: true }]}>
          <Input type="number" />
        </Form.Item>
        <Form.Item label="Forbidden" name="donotuse" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  </div>
    );
};

export default GlossaryManagement;
